import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "./components/ThemeProvider";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { TaalProvider } from "./contexts/TaalContext";
import { BrandingProvider } from "./contexts/BrandingContext";
import { Toaster } from "./components/ui/sonner";
import ChatPage from "./pages/ChatPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ProjectPage from "./pages/ProjectPage";
import ArtifactPage from "./pages/ArtifactPage";
import TeamsPage from "./pages/TeamsPage";
import BillingPage from "./pages/BillingPage";
import NetworkPage from "./pages/NetworkPage";
import StorePage from "./pages/StorePage";
import KnowledgePage from "./pages/KnowledgePage";
import CodePage from "./pages/CodePage";
import MemoryPage from "./pages/MemoryPage";
import PostbusPage from "./pages/PostbusPage";
import SkillsPage from "./pages/SkillsPage";
import AgentsPage from "./pages/AgentsPage";
import PairPage from "./pages/PairPage";
import JoinPage from "./pages/JoinPage";
// The decoder is a quarter of a megabyte of source and lands as 48 kB gzipped.
// Most sessions never open a scanner, and the ones that do are about to talk
// to a signalling service anyway, so it is fetched when it is asked for. The
// service worker keeps hashed chunks, so it is fetched once per version.
const ScanPage = lazy(() => import("./pages/ScanPage"));
import TunnelBanner from "./components/TunnelBanner";
import RequireSession from "./components/RequireSession";
import { startAppointmentSupervisor, stopAppointmentSupervisor } from "./lib/host-link";
import { noteConnection } from "./lib/connection-log";
import { readAppointment } from "./lib/rendezvous";

/** De taalschakelaar hoort te weten wanneer er iemand is: dan leest hij de keuze van de server. */
function TaalVanDeGebruiker({ children }) {
  const { user } = useAuth();
  return <TaalProvider gebruiker={user?.id || null}>{children}</TaalProvider>;
}

function App() {
  // Started here, above every screen, because the thing it guards outlives all
  // of them: a phone that is asleep expects this Mac to be standing in a room
  // whether or not anyone has the Settings dialog open. Deliberately not gated
  // on being the desktop; the appointment itself records which side stored it,
  // so on a phone this finds nothing and does nothing.
  useEffect(() => {
    // MEETLOG, 11 oktober 2026: elke keer dat de pagina (opnieuw) laadt, een
    // regel, met welke kant er een afspraak voor de vervolgkamer bewaard heeft.
    // Geen kamer en geen sleutel in het log, alleen of hij er is.
    const afspraak = readAppointment();
    noteConnection("app-start", afspraak ? `afspraak bewaard (${afspraak.side || "onbekende kant"})` : "geen afspraak bewaard");
    startAppointmentSupervisor();
    return () => stopAppointmentSupervisor();
  }, []);

  return (
    <ThemeProvider defaultTheme="dark" storageKey="novamund-theme">
      <AuthProvider>
        <TaalVanDeGebruiker>
        <BrandingProvider>
        <BrowserRouter>
          {/* Above everything, because when this app is running on somebody's
              Mac over a channel, that is the most important thing on the
              screen: it explains every slow moment and every failure. */}
          <TunnelBanner />
          <Routes>
            {/* The way to become somebody. Reachable with no session at all,
                which is the state a phone arrives in. */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/pair" element={<PairPage />} />
            <Route path="/join" element={<JoinPage />} />

            {/* Everything that only means something once somebody is there. */}
            <Route element={<RequireSession />}>
              <Route path="/" element={<ChatPage />} />
              <Route path="/chat/:conversationId" element={<ChatPage />} />
              <Route path="/project/:projectId" element={<ProjectPage />} />
              <Route path="/project/:projectId/artifact/:artifactId" element={<ArtifactPage />} />
              <Route path="/teams" element={<TeamsPage />} />
              <Route path="/billing" element={<BillingPage />} />
              <Route path="/network" element={<NetworkPage />} />
              <Route path="/store" element={<StorePage />} />
              <Route path="/knowledge" element={<KnowledgePage />} />
              <Route path="/skills" element={<SkillsPage />} />
              <Route path="/code" element={<CodePage />} />
              <Route path="/memory" element={<MemoryPage />} />
              <Route path="/postbus" element={<PostbusPage />} />
              <Route path="/agents" element={<AgentsPage />} />
            </Route>
            <Route
              path="/scan"
              element={
                <Suspense
                  fallback={
                    <div className="fixed inset-0 bg-black flex items-center justify-center text-white/70 text-sm">
                      Opening the camera...
                    </div>
                  }
                >
                  <ScanPage />
                </Suspense>
              }
            />
          </Routes>
        </BrowserRouter>
        </BrandingProvider>
        </TaalVanDeGebruiker>
      </AuthProvider>
      <Toaster position="top-center" richColors />
    </ThemeProvider>
  );
}

export default App;
